import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import invariant from 'tiny-invariant';
import { useShallow } from 'zustand/react/shallow';

import { usePaymentMethod, useUser } from '@mopla/business-logic';
import { PaymentMethodSetupStatusEnum } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';

import { usePaymentEnv } from './context/paymentEnvContext';
import { useInternalStore } from './stores/useInternalStore';
import { usePayByBankSetupStore } from './stores/usePayByBankSetupStore';
import { usePaymentFlowRestoreStore } from './stores/usePaymentFlowRestoreStore';
import { usePaymentStepStore } from './stores/usePaymentStepStore';
import { PayByBankSetupWaiting } from './ui/PayByBankSetupWaiting';
import {
	extractErrorFromTrueLayerUrl,
	fetchPaymentMethodSetupStatus,
	hasUserFeatureFlag,
	parseTrueLayerCallback,
	sanitizeQueryForLogging,
	usePaymentPortalControls,
} from './utils';

/**
 * Component manages the completion logic for adding Payment Methods (PM).
 * Supported PMs: Stripe, TrueLayer.
 * Environments: WEB, Native.
 *
 * Handled scenarios:
 *  * WEB: User was adding a Stripe redirect-based PM and returned after redirect back to the Mopla page
 *  * Native: User was adding a Stripe PM (any type) and completed work with the in-app browser (deeplink opened, landed here)
 *  * WEB, Native: User was adding TrueLayer SEPA, completed authorization, and returned / deeplink opened
 * */
export const PaymentPostProcessor: FC = () => {
	const logger = useLogger('payment');
	const { enableOpenBankingEnv, trueLayerRedirectUrl } = usePaymentEnv();
	const [platform] = useState(Capacitor.getPlatform());
	const navigate = useNavigate();
	const {
		setIsTrueLayerEnabled,
		setStripeError,
		setTrueLayerAuthCode,
		setTrueLayerError,
	} = useInternalStore(
		useShallow((state) => ({
			setTrueLayerReportedAccounts: state.setTrueLayerReportedAccounts,
			setIsTrueLayerEnabled: state.setIsTrueLayerEnabled,
			setStripeError: state.setStripeError,
			setTrueLayerAuthCode: state.setTrueLayerAuthCode,
			setTrueLayerError: state.setTrueLayerError,
		}))
	);
	const { setCurrentPaymentStep, setPaymentPortalOpened } = usePaymentStepStore(
		useShallow((state) => ({
			setCurrentPaymentStep: state.setStep,
			setPaymentPortalOpened: state.setIsOpened,
		}))
	);
	const setIsRestoringState = usePaymentFlowRestoreStore(
		(s) => s.setIsRestoring
	);
	const [query, setQuery] = useSearchParams();
	const { userData } = useUser();
	const { closePaymentPortal } = usePaymentPortalControls();
	const { addStripePMHandler } = usePaymentMethod();
	const hasProcessedRedirectRef = useRef(false);
	/** Read inside the async redirect handler, so a user refresh never recreates the handlers */
	const userIdRef = useRef(userData?.userId);
	userIdRef.current = userData?.userId;

	const isTrueLayerFeatureEnabled = useMemo(() => {
		/** Must be checked only after the user data is loaded. Before that, we just don't know if the feature is enabled or not. */
		if (!userData) return null;

		return Boolean(
			enableOpenBankingEnv && hasUserFeatureFlag(userData, 'openBankingEnabled')
		);
	}, [enableOpenBankingEnv, userData]);

	const isWeb = platform === 'web';

	/**
	 * Handler completes the addition of Stripe PMs.
	 *
	 * Context and invocation conditions.
	 * Called when certain conditions are met, considering platform-specific mechanics (Web, Native):
	 *  * not a TrueLayer redirect
	 *  * isPaymentRedirect=true
	 *
	 * Using Stripe, the user can add 2 types of PMs:
	 *  * Credit Card (Card)
	 *  * Redirect-based PM (Paypal, Klarna, etc.)
	 *
	 * For Native platforms, the handler is used to add all types of PMs (both Card and redirect-based PMs).
	 * For WEB, only redirect-based PM is processed here. (Card PMs are processed in the StripePayment component)
	 *
	 * NATIVE
	 * When adding any PM on Native platforms, an in-app browser opens on top of the app - the user **does not leave** the app context.
	 * Literally, React components remain mounted and alive.
	 * In the in-app browser, the Payment nx app opens (by its own URL - see `environment.paymentBaseUrl` in passenger).
	 * The same flow happens there as in WEB (including bank redirect: Payment app -> Stripe -> Payment app).
	 * At the end of the flow, the Payment app opens a deeplink, the in-app browser closes. The deeplink triggers a Capacitor event `appUrlOpen`.
	 * This deeplink contains data about the added PM:
	 *   * paymentMethodId (when adding Card PM)
	 *   * setupIntentId, redirectStatus (when adding redirect-based PM)
	 *   * paymentClosed (if the in-app browser was closed without adding a PM)
	 *
	 * WEB
	 * When selecting redirect-based PM in Stripe UI, the user leaves the app window, is redirected to a third-party bank authorization page, completes it,
	 * and only then is redirected back to the Mopla page. The Mopla page reopens with a set of query parameters from Stripe.
	 * By their presence, we can determine that a redirect-based PM was added.
	 *
	 *
	 * The final stage of adding Stripe PMs for both platforms is handled identically in this handler.
	 *  * A flag is set signaling that it's time to restore the component state that was closed during the bank redirect in WEB
	 *  * The new PM is saved in Mopla's DB as the default payment method
	 *  * In case of an error during the redirect, the error is handled
	 *  * ‼️Stripe query parameters are cleared (the function returns a new query)
	 * */
	const processStripePaymentRedirect = useCallback(
		(_query: URLSearchParams) => {
			const setupIntentId = _query.get('setup_intent');
			const redirectStatus = _query.get('redirect_status');
			const paymentMethodId = _query.get('paymentMethodId');
			const paymentClosed = _query.get('paymentClosed');

			logger.info(
				'[PaymentPostProcessor] processStripePaymentRedirect is triggered',
				{
					hasSetupIntentId: Boolean(setupIntentId),
					hasPaymentMethodId: Boolean(paymentMethodId),
					redirectStatus,
					paymentClosed,
				}
			);

			// only ios has close method implemented, Android will close the chrome tabs automatically
			if (platform === 'ios') {
				Browser.close();
			}

			if (isWeb && _query.has('isPaymentRedirect')) {
				logger.info(
					'[PaymentPostProcessor] processStripePaymentRedirect: state restore is triggered'
				);

				setIsRestoringState(true);
			}

			/** We close PaymentPortal in any case, except for the error case.
			 * This only makes sense on the Native platform, as in WEB the PaymentPortal will initially be closed after the redirect anyway
			 * */
			if (!isWeb && redirectStatus !== 'failed') {
				closePaymentPortal();
			}

			if (paymentMethodId) {
				addStripePMHandler({ paymentMethodId });
			} else if (
				(redirectStatus === 'succeeded' || redirectStatus === 'processing') &&
				setupIntentId
			) {
				/**
				 * Pay by Bank: the backend sets the setup status to PENDING right after the default PM is set.
				 * Check it once after the handler finished (it never rejects, so only the status check can fail)
				 * and show the waiting overlay if needed.
				 * Fire-and-forget keeps this function synchronous.
				 */
				void (async () => {
					try {
						await addStripePMHandler({ setupIntentId });
						const status = await fetchPaymentMethodSetupStatus();

						if (status === PaymentMethodSetupStatusEnum.PENDING) {
							const userId = userIdRef.current ?? null;
							logger.info(
								'[PaymentPostProcessor] Pay by Bank setup is pending, start waiting',
								{ hasUserId: Boolean(userId) }
							);
							usePayByBankSetupStore.getState().startWaiting(userId);
						}
					} catch (error) {
						logger.error(
							'[PaymentPostProcessor] checking the Pay by Bank setup status failed',
							error
						);
					}
				})();
			} else if (paymentClosed) {
				closePaymentPortal();
			} else if (redirectStatus === 'failed') {
				/**
				 * Stripe Web failure case.
				 * Open the payment portal again, so the user can see the error
				 * */
				logger.warn(
					'[PaymentPostProcessor] stripe redirect returned a failed status',
					{
						platform,
						hasSetupIntentId: Boolean(setupIntentId),
					}
				);

				setCurrentPaymentStep('stripe');
				setStripeError('generic');
				setPaymentPortalOpened(true);
			}

			/**
			 * Warning: do NOT rely on those params in the outside of the payment-flow feature (ex. in BookingFlow), since they get deleted quite early
			 * */
			_query.delete('setup_intent_client_secret');
			_query.delete('setup_intent');
			_query.delete('redirect_status');
			_query.delete('paymentMethodId');
			_query.delete('paymentClosed');
			_query.delete('payment-method');
			_query.delete('isPaymentRedirect');

			return _query;
		},
		[
			platform,
			isWeb,
			setIsRestoringState,
			addStripePMHandler,
			closePaymentPortal,
			setCurrentPaymentStep,
			setStripeError,
			setPaymentPortalOpened,
			logger,
		]
	);

	/**
	 * Handler performs an intermediate step in the process of adding a TrueLayer SEPA PM.
	 * Main handler tasks:
	 *  * Parsing and validating callback parameters from TrueLayer
	 *  * Initiating SepaPayment.tsx rendering (showing account selection UI)
	 *    * Switching payment step to 'sepaCompletion'
	 *    * Saving `code` in zustand store
	 *  * Initiating restoration of the abandoned page context (WEB).
	 *
	 * Invocation context:
	 * Called after the user completes authorization in TrueLayer.
	 * Platform-specific mechanics (Web, Native) are taken into account.
	 * For more details - see README.md
	 *
	 * 1) Restoring the page from which the user navigated to TrueLayer
	 * The process differs from the implementation of the same mechanism for Stripe.
	 *
	 * WEB:
	 * redirect_uri points to the Mopla index page. This is NOT the page where the user was when they initiated adding the TrueLayer SEPA PM.
	 * First, the React app mounts this index page.
	 * To restore the necessary page, from the `state` parameter we take page and query and perform react-router navigate -> the needed page is mounted.
	 * Also, in the zustand store an `isRestoring=true` flag is set - by this flag the parent page's React component will understand that it needs to retrieve data from persistent state (localStorage)
	 *
	 * Native:
	 * Since the user didn't leave the page context, we skip this step - no page and context restoration is needed.
	 *
	 * 2) Retrieving the list of bank accounts
	 * An exchange of auth code for a list of accounts happens through Mopla's BE.
	 * We save this list in the zustand store for subsequent use in the UI
	 * */
	const processTrueLayerPaymentRedirect = useCallback(
		async (_query: URLSearchParams) => {
			logger.info(
				'[PaymentPostProcessor] processTrueLayerPaymentRedirect is triggered'
			);

			invariant(
				isTrueLayerFeatureEnabled,
				'[PaymentPostProcessor] processTrueLayerPaymentRedirect: TrueLayer feature is not enabled'
			);

			invariant(
				trueLayerRedirectUrl,
				'[PaymentPostProcessor] processTrueLayerPaymentRedirect: trueLayerRedirectUrl is not set'
			);

			/** Parse and validate the query that was passed from TrueLayer Authorization */
			let parsedCallback;
			let isCriticalError = false;

			try {
				parsedCallback = parseTrueLayerCallback(_query);
			} catch (criticalError) {
				/**
				 * Critical error - state is missing or invalid, cannot restore the page.
				 * Use fallback state to redirect to default page (profile page payment details screen).
				 */
				logger.error('[PaymentPostProcessor] criticalError', criticalError, {
					query: sanitizeQueryForLogging(_query),
				});

				parsedCallback = {
					code: null,
					error: null,
					state: {
						restorePage: '/home/profile',
						query: 'paymentDetails',
					},
				};
				isCriticalError = true;
			}

			const { code, error, state } = parsedCallback;

			/** Log error to Sentry if present */
			if (error) {
				logger.error('[PaymentPostProcessor] error', error, {
					state,
				});
			}
			/**
			 * Switch the step to `sepaCompletion`
			 * This triggers SepaPayment.tsx rendering, where the SEPA PM addition will be completed.
			 * */
			setCurrentPaymentStep('sepaCompletion');

			/**
			 * When in web after the TrueLayer redirect, the payment portal is closed, so we need to reopen it.
			 * In the Native env, the payment portal should remain opened, however this is the extra safety layer.
			 * */
			setPaymentPortalOpened(true);

			if (isWeb) {
				/** We restore state only in the web env, because the native flows (both TrueLayer and Stripe) don't do redirects */
				if (!isCriticalError) {
					setIsRestoringState(true);
				}

				logger.info(
					'[PaymentPostProcessor] processTrueLayerPaymentRedirect: navigating to restore page',
					{ pathname: state.restorePage, search: state.query }
				);

				/** TODO Sentry is scrabbing sensitive data (code) here. Need to investigate how to eliminate that. */
				navigate(
					{
						pathname: state.restorePage,
						search: state.query,
					},
					{ replace: true }
				);
			}

			if (platform === 'ios') {
				Browser.close();
			}

			/** Handle TrueLayer callback: success (code), TrueLayer error, or validation error */
			if (code && !error) {
				logger.info(
					'[PaymentPostProcessor] processTrueLayerPaymentRedirect: TrueLayer auth code received'
				);

				setTrueLayerAuthCode(code);
			} else if (error || isCriticalError) {
				setTrueLayerError(true);
			}

			return;
		},
		[
			isTrueLayerFeatureEnabled,
			isWeb,
			navigate,
			platform,
			setCurrentPaymentStep,
			setIsRestoringState,
			setPaymentPortalOpened,
			setTrueLayerAuthCode,
			setTrueLayerError,
			trueLayerRedirectUrl,
			logger,
		]
	);

	useEffect(() => {
		/** Wait until the TrueLayer featureflag is properly checked. It depends on the user data, so we need to wait until it is fetched. */
		if (isTrueLayerFeatureEnabled == null) return;
		/** Ensure this effect only processes the redirect once */
		if (hasProcessedRedirectRef.current) return;
		hasProcessedRedirectRef.current = true;

		const isTruelayerRedirect = query.has('truelayer_redirect');
		const hasTruelayerCode = query.has('code');
		const hasTruelayerError = Boolean(extractErrorFromTrueLayerUrl(query));
		const isPaymentRedirect = query.has('isPaymentRedirect');

		if (
			isTruelayerRedirect &&
			(hasTruelayerCode || hasTruelayerError) &&
			isWeb
		) {
			processTrueLayerPaymentRedirect(query);
		} else if (isPaymentRedirect && isWeb) {
			const updatedQuery = processStripePaymentRedirect(query);
			setQuery(updatedQuery);
		} else {
			/** In case of fresh empty, just initialize the payment step properly */
			setCurrentPaymentStep(isTrueLayerFeatureEnabled ? 'selection' : 'stripe');
		}

		/** Trigger once after isTrueLayerFeatureEnabled is initialized (after user data is loaded) */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTrueLayerFeatureEnabled]);

	useEffect(() => {
		if (isWeb) return;

		let listener: PluginListenerHandle | null = null;

		const setupListener = async () => {
			listener = await CapacitorApp.addListener(
				'appUrlOpen',
				(event: URLOpenListenerEvent) => {
					const url = new URL(event.url);

					const isTruelayerRedirect =
						url.searchParams.has('truelayer_redirect');
					const hasTruelayerCode = url.searchParams.has('code');
					const hasTruelayerError = Boolean(extractErrorFromTrueLayerUrl(url));
					const isPaymentRedirect = url.searchParams.has('isPaymentRedirect');

					if (isTruelayerRedirect && (hasTruelayerCode || hasTruelayerError)) {
						processTrueLayerPaymentRedirect(url.searchParams);
					} else if (isPaymentRedirect) {
						processStripePaymentRedirect(url.searchParams);
					}
				}
			);
		};

		setupListener();

		return () => {
			if (listener) {
				listener.remove();
			}
		};
		/** Stripe & TrueLayer handlers must be included into the deps, bcs they use variables from outside (stale closure problem)) */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processStripePaymentRedirect, processTrueLayerPaymentRedirect]);

	/**
	 * Listen for the `browserFinished` event (this happens when the in-app browser gets closed) and close the payment portal.
	 * */
	useEffect(() => {
		let l1: PluginListenerHandle | null = null;

		const setupListener = async () => {
			l1 = await Browser.addListener('browserFinished', () => {
				const currentPaymentStep = usePaymentStepStore.getState().step;

				/**
				 * We cannot rely on the browserFinished event to detect if the browser was closed by a user intention (direct action).
				 * Instead, the browser can be closed by the system automatically, which sometimes trigger this event also (was discovered in the Android env).
				 * That's why we do not close the payment portal in the `sepaAuthentication` and `sepaCompletion` steps.
				 * On Android, after the TrueLayer deeplink fires (setting step to `sepaCompletion`), the browser
				 * also fires `browserFinished` automatically - which would undo the step transition.
				 * This doesn't work perfectly from the UX point of view, but it is safer to do it in this way.
				 * */
				if (
					currentPaymentStep !== 'sepaAuthentication' &&
					currentPaymentStep !== 'sepaCompletion'
				) {
					closePaymentPortal();
				}
			});
		};

		setupListener();

		return () => {
			if (l1) {
				l1.remove();
			}
		};
	}, [closePaymentPortal]);

	useEffect(() => {
		if (isTrueLayerFeatureEnabled != null) {
			setIsTrueLayerEnabled(isTrueLayerFeatureEnabled);
		}
	}, [isTrueLayerFeatureEnabled, setIsTrueLayerEnabled]);

	return <PayByBankSetupWaiting />;
};
